import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { cn } from '@/lib/cn';

async function writeToClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const field = document.createElement('textarea');
  field.value = text;
  field.setAttribute('readonly', '');
  field.style.position = 'fixed';
  field.style.opacity = '0';
  document.body.appendChild(field);
  field.select();
  document.execCommand('copy');
  document.body.removeChild(field);
}

export function useCopy(): { copied: boolean; copy: (text: string) => Promise<void> } {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => (timer.current ? clearTimeout(timer.current) : undefined), []);

  const copy = useCallback(async (text: string) => {
    await writeToClipboard(text);
    setCopied(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 1500);
  }, []);

  return { copied, copy };
}

/** Small button that copies one value and says "Copied" for a second and a half. */
export function CopyButton({ value, label, className }: { value: string; label: string; className?: string }) {
  const { copied, copy } = useCopy();
  return (
    <button
      type="button"
      aria-label={copied ? `${label} copied` : `Copy ${label}`}
      className={cn(
        'inline-flex h-7 shrink-0 items-center gap-1 rounded-full px-1.5 text-xs font-medium',
        'transition-[background-color,color] duration-150 ease-out',
        copied
          ? 'bg-[color:var(--ts-pill-success-bg)] text-[color:var(--ts-pill-success-text)]'
          : 'text-gray-1 hover:bg-[color:var(--ts-row-hover)] hover:text-primary',
        className,
      )}
      onClick={(event) => {
        event.stopPropagation();
        void copy(value);
      }}
    >
      {copied ? (
        <>
          <Check className="h-3.5 w-3.5" aria-hidden="true" />
          Copied
        </>
      ) : (
        <Copy className="h-3.5 w-3.5" aria-hidden="true" />
      )}
    </button>
  );
}

/** A value with a copy button beside it. */
export function CopyValue({ value, label, className }: { value: string; label: string; className?: string }) {
  return (
    <span className={cn('inline-flex items-center gap-1.5', className)}>
      <span className="text-base text-gray-3">{value}</span>
      <CopyButton value={value} label={label} />
    </span>
  );
}
